import { CloseIcon, DeleteIcon } from 'assets'
import { useDisclosure, useHandleApiResponse } from 'hooks'

import Modal from './modal'
import PropTypes from 'prop-types'
import { alert } from 'utilities'
import { getVideoEmbedUrl, getVideoThumbnail } from 'utilities/video'
import { useDeleteMediaMutation } from 'services/media.service'

const PlayIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white' className='w-12 h-12 drop-shadow-lg'>
    <path
      fillRule='evenodd'
      d='M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z'
      clipRule='evenodd'
    />
  </svg>
)

const GalleryMedia = ({ title, media_link, id, isAdmin, type, video_url }) => {
  const { isOpen: isMediaOpened, open: openMedia, close: closeMedia } = useDisclosure()

  const [deleteMedia, { isLoading: isDeletingMedia, ...response }] = useDeleteMediaMutation()

  const handleDelete = () => {
    deleteMedia(id)
  }

  const handleDeleteSuccess = () => {
    alert.success('Media deleted successfully.')
  }

  useHandleApiResponse({
    handleSuccess: handleDeleteSuccess,
    ...response,
  })

  const isVideo = type === 'video'
  const embedUrl = isVideo ? getVideoEmbedUrl(video_url) : null
  const thumbnailUrl = isVideo ? getVideoThumbnail(video_url) : null

  const labelAndActionNode = (
    <div className={`flex ${isAdmin ? 'justify-between' : 'justify-start'} mt-3`}>
      <p className='text-sm text-[#333] leading-[140%]'>{title}</p>

      {isAdmin && (
        <button disabled={isDeletingMedia} onClick={handleDelete}>
          <DeleteIcon />
        </button>
      )}
    </div>
  )

  // Render the thumbnail/preview card
  const renderPreview = () => {
    if (isVideo) {
      return (
        <div
          className='relative rounded-[5px] h-[200px] xs:h-[280px] w-full cursor-pointer border border-solid border-[#eee] overflow-hidden bg-black'
          onClick={openMedia}
        >
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt={title} className='w-full h-full object-cover opacity-80' />
          ) : (
            // Fallback: show a small embedded preview (non-interactive, just for visual)
            <div className='w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1a1a2e] to-[#16213e]'>
              <div className='text-center'>
                <PlayIcon />
                <p className='text-white/70 text-xs mt-2'>Video</p>
              </div>
            </div>
          )}
          {/* Play button overlay */}
          <div className='absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors'>
            <div className='bg-black/50 rounded-full p-3'>
              <PlayIcon />
            </div>
          </div>
        </div>
      )
    }

    return (
      <img
        src={media_link}
        alt={title}
        className='rounded-[5px] h-[200px] xs:h-[280px] object-cover w-full cursor-pointer border border-solid border-[#eee]'
        onClick={openMedia}
      />
    )
  }

  // Render the modal content
  const renderModalContent = () => {
    if (isVideo && embedUrl) {
      return (
        <div className='w-full h-full flex flex-col justify-between'>
          <div className='flex justify-end mb-3'>
            <button onClick={closeMedia}>
              <CloseIcon className='h-5 w-5' />
            </button>
          </div>
          <iframe
            src={embedUrl}
            title={title}
            className='rounded-[5px] h-[calc(100%_-_50px)] w-full border border-solid border-[#eee]'
            sandbox='allow-scripts allow-same-origin allow-presentation allow-popups'
            allow='accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share'
            allowFullScreen
            referrerPolicy='strict-origin-when-cross-origin'
          />
          {labelAndActionNode}
        </div>
      )
    }

    return (
      <div className='w-full h-full flex flex-col justify-between'>
        <div className='flex justify-end mb-3'>
          <button onClick={closeMedia}>
            <CloseIcon className='h-5 w-5' />
          </button>
        </div>
        <img src={media_link} alt={title} className='rounded-[5px] h-[calc(100%_-_50px)] border border-solid border-[#eee] w-full object-contain' />

        {labelAndActionNode}
      </div>
    )
  }

  return (
    <>
      <div className='w-full'>
        {renderPreview()}
        {labelAndActionNode}
      </div>
      <Modal isOpen={isMediaOpened} dialogClassName={'!w-[1300px] !max-w-full !h-[800px] max-h-[100vh] '} close={closeMedia}>
        {renderModalContent()}
      </Modal>
    </>
  )
}

export default GalleryMedia

GalleryMedia.propTypes = {
  title: PropTypes.string,
  media_link: PropTypes.string,
  id: PropTypes.number,
  isAdmin: PropTypes.bool,
  type: PropTypes.string,
  video_url: PropTypes.string,
}
